import * as validation from '@/class/common/validation';
import { INPUT_TYPE } from '@/components/util/input_type';

export const CHECK_TYPE = validation.CHECK_TYPE;

export interface IPValidation extends validation.IValidation {
  type: INPUT_TYPE;
  name: string;
  placeholder?: string;
  error?: string;
}

/**
 * 入力値チェック（複数）
 * @param values
 * @param items
 * @returns エラーがでたvalues(UIコントロール)の数
 */
export function checkValues(values: any, items: IPValidation[]): number {
  let resultValidation = 0;
  for (const key of Object.keys(values)) {
    const foundItems = items.filter((v: any) => v.key === key);
    if (foundItems.length === 1) {
      resultValidation = checkValue(values[key], foundItems[0])
        ? resultValidation + 1
        : resultValidation;
    } else if (foundItems.length <= 0) {
      // items[key]がない場合はスキップ
      // console.warn(`key(${key})のitemが見つかりません。`);
    } else {
      console.error(`同じkey(${key})のitemが複数あります。`);
    }
  }
  return resultValidation;
}

/**
 * 入力値チェック（単体）
 * @param value
 * @param item
 * @returns エラーならtrue
 */
export function checkValue(value: any, item: IPValidation): boolean {
  // エラーをクリア
  item.error = '';

  // UIコントロールの種類存在チェック
  if (!item.type) {
    console.error('item.typeが未設定です。');
    return false;
  }

  // UIコントロールの種類ごとの値検証
  switch (item.type) {
    case INPUT_TYPE.TEXT:
    case INPUT_TYPE.PASSWORD:
    case INPUT_TYPE.TEXTAREA:
    case INPUT_TYPE.TIME:
    case INPUT_TYPE.DATE:
      if (checkInputTextFormat(value, item)) {
        return true;
      }
      break;
    case INPUT_TYPE.NUMBER:
      if (checkInputNumber(value, item)) {
        return true;
      }
      break;
    case INPUT_TYPE.RADIO:
    case INPUT_TYPE.SELECT:
    case INPUT_TYPE.CHECK:
    case INPUT_TYPE.SEARCH:
    case INPUT_TYPE.TEXT_TAG:
      // 検証はしない。
      return false;
    default:
      console.error(`サポートされていないitem.type(${item.type})です。`);
      return false;
  }

  // 文字列オプションチェック
  if (validateStringOptions(value, item)) {
    return true;
  }

  return false;
}

/**
 * [UIコントロール値検証] テキスト形式の入力コントロール用
 * INPUT_TYPE.TEXT
 * INPUT_TYPE.DATE
 * INPUT_TYPE.TIME
 * INPUT_TYPE.PASSWORD
 * など
 */
function checkInputTextFormat(value: any, item: IPValidation): boolean {
  // 空白チェック
  if (validateStringSpace(value, item)) {
    return true;
  }
  // 必須項目チェック
  if (validateStringRequired(value, item)) {
    return true;
  }
  // 文字列長チェック
  if (validateStringLength(value, item)) {
    return true;
  }
  // 絵文字チェック
  if (validateStringEmoji(value, item)) {
    return true;
  }

  return false;
}

/**
 * [UIコントロール値検証] INPUT_TYPE.NUMBER 用
 */
function checkInputNumber(value: any, item: IPValidation): boolean {
  if (typeof value === 'string') {
    // スペース文字チェック
    if (validateStringSpace(value, item)) {
      return true;
    }
  }
  // 必須項目チェック
  if (validateStringRequired(value, item)) {
    return true;
  }

  // 数値の場合は必須項目のときのみチェック。必須項目でないときは無入力でかまわない。
  if (item.required) {
    // 数字に変換できなければエラー
    if (Number.isNaN(Number(value))) {
      item.error += '数字を入力してください。';
      return true;
    }

    // 最小値、最大値チェック
    if (validateNumberMinMax(value, item)) {
      return true;
    }
  }

  return false;
}

/**
 * [値検証] スペース文字チェック
 * @param value
 * @param item
 * @returns エラーならtrue
 */
function validateStringSpace(value: string, item: IPValidation): boolean {
  const spaceRemovedString = value.replace(/\s+/g, '');
  let isError = false;
  if (!spaceRemovedString && value.length > 0) {
    item.error = '空白のみの入力はできません。';
    isError = true;
  }
  return isError;
}

/**
 * 必須項目チェック
 * @param value
 * @param item
 * @returns エラーならtrue
 */
function validateStringRequired(value: string, item: IPValidation): boolean {
  if (item.required && value.length <= 0) {
    item.error = '必須項目です。 ';
    return true;
  }
  return false;
}

/**
 * [値検証] 文字列長チェック
 * @param value
 * @param item
 * @returns エラーならtrue
 */
function validateStringLength(value: string, item: IPValidation): boolean {
  const valueLength = value.length;

  // 最大文字数
  if (item.max) {
    if (item.max < valueLength) {
      item.error += `${item.max}文字以下で入力してください。`;
      return true;
    }
  }

  // 最小文字数
  if (item.min) {
    if (item.min > valueLength) {
      item.error += `${item.min}文字以上で入力してください。`;
      return true;
    }
  }

  return false;
}

/**
 * [値検証] 絵文字チェック
 * @param value
 * @param item
 * @returns エラーならtrue
 */
function validateStringEmoji(value: string, item: IPValidation): boolean {
  const error = validation.hasEmoji(value, 'emoji');
  if (error === '') {
    return false;
  }
  item.error += error;
  return true;
}

/**
 * [値検証] 文字列追加チェック
 * @param value
 * @param item
 */
function validateStringOptions(value: string, item: IPValidation): boolean {
  // 追加チェックは文字が入力されていたらチェックする。
  if (value.length <= 0) {
    return false;
  }

  let isError = false;
  if (item.checks) {
    for (const check of item.checks) {
      const errMsg = validation.checkOption(value, check);
      if (errMsg && errMsg.length > 0) {
        item.error += errMsg;
        isError = true;
      }
    }
  }
  return isError;
}

/**
 * [値検証] 数値の最小値、最大値チェック
 * @param value
 * @param item
 */
function validateNumberMinMax(value: number, item: IPValidation) {
  // 最大値
  if (item.max) {
    if (item.max < value) {
      item.error += `${item.max}以下で入力してください。`;
      return true;
    }
  }
  // 最小値
  if (item.min || item.min === 0) {
    if (item.min > value) {
      item.error += `${item.min}以上で入力してください。`;
      return true;
    }
  }
  return false;
}
